/*
 * Email capture under the buy buttons. Two modes, picked from data-available:
 *
 *   sold out  -- "email me if this comes back": a Klaviyo back-in-stock subscription for
 *                this variant, plus the marketing list.
 *   in stock  -- the marketing list on its own. Most of the catalogue is a one-off surplus
 *                lot, so the next one of these is a new arrival rather than a restock, and
 *                there is nothing variant-specific to promise.
 *
 * The visible copy is generated from what the code actually does: no list id configured
 * means no list sentence, and the in-stock form does not render at all, because a form
 * that signs you up for nothing has nothing to say. Once someone has joined the list we
 * stop asking on in-stock pages (sold-out pages still offer the stock alert, which is a
 * different thing).
 *
 * Sold-out mode requires "Back in Stock" to be enabled in the Klaviyo account and a Live
 * flow behind it; without the flow, addresses are collected and never emailed.
 */
(function () {
  var SIGNUP_FLAG = 'tcp:list-signup';

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else { fn(); }
  }

  function alreadyOnList() {
    try { return window.localStorage.getItem(SIGNUP_FLAG) === '1'; } catch (e) { return false; }
  }

  function rememberSignup() {
    try { window.localStorage.setItem(SIGNUP_FLAG, '1'); } catch (e) { /* private mode */ }
  }

  ready(function () {
    var root = document.querySelector('[data-email-capture], [data-back-in-stock]');
    if (!root) return;

    var company = root.getAttribute('data-klaviyo-company');
    if (!company) return;

    var variantId = root.getAttribute('data-variant-id');
    var inStock = root.getAttribute('data-available') === 'true';

    // comma-separated so one signup can join several lists
    var listIds = (root.getAttribute('data-klaviyo-list') || '')
      .split(',').map(function (x) { return x.trim(); }).filter(Boolean);
    var joinsList = listIds.length > 0;

    var title, note, button, success;

    if (inStock) {
      // Nothing to offer without a list, and no reason to ask someone who already joined.
      if (!joinsList || alreadyOnList()) return;
      title = 'Get the next lot first';
      note = 'Our stock comes from surplus lots, so most of what you see here is a one-off. ' +
             'Join our email list and we&rsquo;ll send you new arrivals and deals as they land. ' +
             'Unsubscribe anytime.';
      button = 'Sign me up';
      success = "You're on the list. We'll email you when new arrivals land.";
    } else {
      if (!variantId) return;
      title = 'Email me if this comes back';
      note = "Our stock comes from surplus lots, so we can't say when a model will turn up " +
             "again. We'll email you once if this one does";
      note += joinsList
        ? ", and we'll add you to our list for deals and new arrivals. Unsubscribe anytime."
        : " &mdash; no list signup.";
      button = 'Notify me';
      success = joinsList
        ? "You're signed up. We'll email you if this one comes back in."
        : "You're on the list. We'll email you if this one comes back in.";
    }

    root.innerHTML =
      '<h2 class="bis__title h4">' + title + '</h2>' +
      '<p class="bis__note">' + note + '</p>' +
      '<form class="bis__form" novalidate>' +
        '<label class="bis__label" for="bis-email">Your email</label>' +
        '<div class="bis__row">' +
          '<input class="bis__input" id="bis-email" type="email" name="email" ' +
                 'placeholder="you@example.com" autocomplete="email" required>' +
          '<button class="bis__button button" type="submit">' + button + '</button>' +
        '</div>' +
      '</form>' +
      '<p class="bis__status" role="status" aria-live="polite"></p>';

    root.hidden = false;

    var form = root.querySelector('.bis__form');
    var input = root.querySelector('.bis__input');
    var submit = root.querySelector('.bis__button');
    var status = root.querySelector('.bis__status');

    // Klaviyo's modern client-side API. The legacy /onsite/components/ endpoint sends no
    // CORS headers, so a browser call to it fails before it ever reaches Klaviyo.
    var REVISION = '2024-10-15';

    function klaviyo(path, payload) {
      return fetch('https://a.klaviyo.com/client/' + path + '/?company_id=' + encodeURIComponent(company), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', revision: REVISION },
        body: JSON.stringify(payload)
      }).then(function (r) {
        if (!r.ok) throw new Error(path + ' returned ' + r.status);
        return r;
      });
    }

    function subscribe(profile) {
      return Promise.all(listIds.map(function (id) {
        return klaviyo('subscriptions', {
          data: {
            type: 'subscription',
            attributes: { profile: profile },
            relationships: { list: { data: { type: 'list', id: id } } }
          }
        });
      }));
    }

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var email = (input.value || '').trim();
      if (!email || email.indexOf('@') < 1) {
        status.textContent = 'Please enter a valid email address.';
        return;
      }

      submit.disabled = true;
      status.textContent = 'Signing you up…';

      var profile = { data: { type: 'profile', attributes: { email: email } } };

      var request;
      if (inStock) {
        // The list is the whole signup here, so a failed subscription is a failed signup.
        request = subscribe(profile);
      } else {
        request = klaviyo('back-in-stock-subscriptions', {
          data: {
            type: 'back-in-stock-subscription',
            attributes: { profile: profile, channels: ['EMAIL'] },
            relationships: {
              variant: { data: { type: 'catalog-variant', id: '$shopify:::$default:::' + variantId } }
            }
          }
        }).then(function () {
          if (!joinsList) return;
          // The stock alert is registered either way, so don't fail the signup over the list.
          return subscribe(profile).catch(function () {});
        });
      }

      request
        .then(function () {
          if (joinsList) rememberSignup();
          form.hidden = true;
          status.textContent = success;
        })
        .catch(function () {
          submit.disabled = false;
          status.textContent = "That didn't go through. Please try again in a moment.";
        });
    });
  });
})();
